import React, { useContext, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import TagManager from 'react-gtm-module'
import OrganizationsContext from 'containers/organizations/organizationsContext'
import { MCP_SERVER_URL, MCP_DOCS_URL, MCP_URL_COPIED_EVENT, mcpUrlCopiedKey } from 'constants/mcp'

/**
 * Maya MCP onboarding guide. Rendered in two places:
 *  - variant="full"    → the "Ask Maya" tab of the Data & Insights flow (complete reference)
 *  - variant="compact" → the Shopify post-purchase "ready" state (essentials + link to full)
 *
 * Content is intentionally static and identical for every org (room for per-org
 * personalization later). Claude is the worked example; the same MCP URL works in other
 * AI agent environments (ChatGPT, Microsoft Copilot Studio).
 */

interface OnboardingGuideProps {
  variant?: 'full' | 'compact'
  // Used by the compact variant to link through to the full guide in settings.
  organizationSlug?: string
}

interface StarterPrompt {
  title: string
  rationale: string
  prompt: string
}

const STARTER_PROMPTS: StarterPrompt[] = [
  {
    title: 'Shopify performance',
    rationale: 'How your store is trending — revenue, orders, and anything out of the ordinary.',
    prompt:
      'How did my Shopify revenue and orders trend over the last 90 days? Call out anything unusual and what might be driving it.',
  },
  {
    title: 'Weekly performance check',
    rationale: 'What changed across your channels week-over-week, and what to pay attention to.',
    prompt:
      'Give me a weekly performance snapshot across all my channels. Highlight anything that moved meaningfully week-over-week.',
  },
  {
    title: 'Find growth opportunities',
    rationale: 'Where you can grow — keywords you are missing, queries competitors win.',
    prompt:
      "Where are my biggest growth opportunities right now? Look across SEO and SEM — keywords I'm not capturing, queries where competitors outrank me.",
  },
  {
    title: 'Diagnose a drop',
    rationale: 'Traffic, conversions, or revenue dropped — figure out why before the next standup.',
    prompt:
      'My traffic dropped last week. Help me figure out what happened — which channel, which campaigns, which queries.',
  },
]

interface CategoryRow {
  category: string
  covers: string
  ask: string
}

const CATEGORY_REFERENCE: CategoryRow[] = [
  { category: 'Shopify', covers: 'Ecommerce: orders, revenue, customers, cohort + lifecycle', ask: 'How did my Shopify revenue trend over the last 90 days?' },
  { category: 'SEO', covers: 'Organic search performance from Google Search Console', ask: 'What were my top SEO queries by clicks in the last 30 days?' },
  { category: 'SEM', covers: 'Paid search performance', ask: "What's my SEM cost-per-conversion by campaign this month?" },
  { category: 'GA4', covers: 'Web and app analytics, channel attribution', ask: 'Show me my GA4 traffic breakdown by channel last week.' },
  { category: 'Facebook', covers: 'Facebook Ads (paid) analytics', ask: "What's my Facebook ROAS by campaign in the last 30 days?" },
  { category: 'FB Organic', covers: 'Facebook organic content analytics (engagement, reach, growth)', ask: 'Show me my Facebook organic engagement this month.' },
  { category: 'YouTube', covers: 'YouTube channel analytics', ask: 'How are my YouTube videos performing this month?' },
  { category: 'Email Marketing', covers: 'Email campaigns (open rates, clicks, conversions)', ask: 'How did my email campaigns perform this month?' },
  { category: 'App', covers: 'Mobile app analytics (installs, sessions, in-app events)', ask: "What's my app install trend over the last 30 days?" },
  { category: 'Competition', covers: 'Competitor SEO + keyword opportunity data', ask: "Which competitors are ranking for keywords I'm missing?" },
  { category: 'Spend', covers: 'Cross-channel paid media spend rollup', ask: 'Show total paid media spend by channel this month.' },
  { category: 'TV (select clients)', covers: 'TV advertising, broadcaster data, share of voice', ask: "What's my TV share of voice vs my main competitors this month?" },
]

const PROMPTING_TIPS: Array<{ lead: string; body: string }> = [
  { lead: 'Be specific about the timeframe.', body: '“Last 7 days”, “Q1”, or “vs last month” beats “recently”.' },
  { lead: 'Name the channel if you know it.', body: '“SEM Google campaigns” gets you a sharper answer than “my ads”.' },
  { lead: 'Follow-ups are encouraged.', body: 'Drill down by competitor, by query, by date — the assistant keeps the context.' },
  { lead: 'Ask for the why, not just the what.', body: '“Why did conversions drop?” surfaces contributing factors, not just the number.' },
]

// ---- small copy helpers --------------------------------------------------

const useCopied = () => {
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear any pending reset on unmount so we never setState on an unmounted component.
  useEffect(() => () => { if (timeoutRef.current) clearTimeout(timeoutRef.current) }, [])

  const copy = async (text: string, onCopied?: () => void) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch (e) {
      // Clipboard can reject (insecure context / permission denied) — don't show a false "Copied".
      console.error('Failed to copy to clipboard:', e)
      return
    }
    setCopied(true)
    onCopied?.()
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => setCopied(false), 2000)
  }
  return { copied, copy }
}

export const CopyUrlBlock: React.FC<{ url: string; onCopied?: () => void }> = ({ url, onCopied }) => {
  const { copied, copy } = useCopied()
  const { currentOrganization } = useContext(OrganizationsContext)

  // A successful copy marks the "Ask Maya" step of the Data & Insights flow as done
  // (per-org key), and notifies an already-mounted flow in the same session.
  const handleCopied = () => {
    try {
      localStorage.setItem(mcpUrlCopiedKey(currentOrganization?.id), 'true')
      window.dispatchEvent(new Event(MCP_URL_COPIED_EVENT))
    } catch (e) {
      // localStorage can be unavailable (private mode) — the flow just won't mark the step.
    }
    onCopied?.()
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: 'white',
        border: '1px solid #c3d8bf',
        borderRadius: '6px',
        padding: '8px 12px',
      }}
    >
      <code style={{ flex: 1, fontSize: '0.85rem', color: '#333', wordBreak: 'break-all' }}>{url}</code>
      <button
        onClick={() => copy(url, handleCopied)}
        title="Copy to clipboard"
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', flexShrink: 0 }}
      >
        <span className="material-icons-outlined" style={{ fontSize: '18px', color: copied ? '#4caf50' : '#739570' }}>
          {copied ? 'check' : 'content_copy'}
        </span>
      </button>
    </div>
  )
}

const PromptCard: React.FC<{ item: StarterPrompt }> = ({ item }) => {
  const { copied, copy } = useCopied()
  return (
    <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '1rem', backgroundColor: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem' }}>
        <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '0.95rem', color: '#333' }}>{item.title}</h4>
        <button
          onClick={() => copy(item.prompt)}
          title="Copy prompt"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0,
            background: 'none', border: '1px solid #c3d8bf', borderRadius: '6px',
            padding: '2px 8px', cursor: 'pointer', color: copied ? '#4caf50' : '#739570', fontSize: '0.8rem',
          }}
        >
          <span className="material-icons-outlined" style={{ fontSize: '15px' }}>{copied ? 'check' : 'content_copy'}</span>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.82rem', color: '#6c757d' }}>{item.rationale}</p>
      <p
        style={{
          margin: 0, fontSize: '0.85rem', color: '#37474f', fontStyle: 'italic',
          borderLeft: '3px solid #c3d8bf', paddingLeft: '10px', lineHeight: 1.5,
        }}
      >
        {item.prompt}
      </p>
    </div>
  )
}

const SetupSteps: React.FC = () => (
  <ol style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem', color: '#37474f', fontSize: '0.9rem', lineHeight: 1.7 }}>
    <li>In Claude, open <strong>Settings → Connectors → Add custom connector</strong>.</li>
    <li>Paste the MCP URL above and confirm. Claude opens your browser — sign in to Maya Insights to authorize the connection.</li>
    <li>Open a new chat. You’re connected.</li>
  </ol>
)

// ---- section heading -----------------------------------------------------

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 style={{ margin: '2rem 0 0.75rem 0', fontSize: '1.1rem', color: '#222', borderLeft: '3px solid #739570', paddingLeft: '0.6rem', lineHeight: 1.3 }}>
    {children}
  </h3>
)

// ---- main component ------------------------------------------------------

const OnboardingGuide: React.FC<OnboardingGuideProps> = ({ variant = 'full', organizationSlug }) => {
  if (variant === 'compact') {
    return (
      <div style={{ textAlign: 'left' }}>
        {/* MCP URL */}
        <div style={{ backgroundColor: '#e8f5e7', borderRadius: '8px', padding: '16px 20px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#739570' }}>link</span>
            <span style={{ fontWeight: 600, color: '#739570', fontSize: '0.9rem' }}>Your MCP Server</span>
          </div>
          <p style={{ color: '#555', fontSize: '0.85rem', margin: '0 0 10px 0', lineHeight: 1.4 }}>
            Connect Claude (or another AI assistant such as ChatGPT or Copilot Studio) to your data with this URL:
          </p>
          <CopyUrlBlock
            url={MCP_SERVER_URL}
            onCopied={() => TagManager.dataLayer({ dataLayer: { event: 'clicked_copy_mcp_url', is_custom_track: true } })}
          />
        </div>

        {/* Setup */}
        <div style={{ marginBottom: '16px' }}>
          <strong style={{ fontSize: '0.9rem', color: '#333' }}>Connect in 3 steps</strong>
          <SetupSteps />
        </div>

        {/* A couple of starter prompts */}
        <div style={{ marginBottom: '16px' }}>
          <strong style={{ fontSize: '0.9rem', color: '#333' }}>Try asking</strong>
          <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.5rem' }}>
            {STARTER_PROMPTS.slice(0, 2).map((p) => <PromptCard key={p.title} item={p} />)}
          </div>
        </div>

        {organizationSlug && (
          <Link
            to={`/organization/${organizationSlug}/data-insights/ask-maya`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#739570', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 500 }}
          >
            Full setup guide
            <span className="material-icons-outlined" style={{ fontSize: '18px' }}>arrow_forward</span>
          </Link>
        )}
      </div>
    )
  }

  // ----- full -----
  return (
    <div style={{ marginTop: '0.5rem', width: '100%', maxWidth: '900px', boxSizing: 'border-box' }}>
      {/* Setup */}
      <SectionHeading>Setup</SectionHeading>
      <div style={{ backgroundColor: '#e8f5e7', borderRadius: '8px', padding: '16px 20px' }}>
        <p style={{ color: '#555', fontSize: '0.9rem', margin: '0 0 10px 0' }}>
          <strong>Prerequisites:</strong> the Claude app (desktop or web) and an active Maya Insights account. Then add Maya
          as a custom connector using this URL:
        </p>
        <CopyUrlBlock
          url={MCP_SERVER_URL}
          onCopied={() => TagManager.dataLayer({ dataLayer: { event: 'clicked_copy_mcp_url', is_custom_track: true } })}
        />
        <SetupSteps />
        <p style={{ color: '#555', fontSize: '0.85rem', margin: '0.75rem 0 0 0', lineHeight: 1.5 }}>
          <strong>Using a different assistant?</strong> The same URL works in other AI agent environments such as ChatGPT
          and Microsoft Copilot Studio — add it as a custom MCP connector there.
        </p>
      </div>

      {/* Verify */}
      <SectionHeading>Verify it worked</SectionHeading>
      <p style={{ fontSize: '0.9rem', color: '#37474f', lineHeight: 1.6 }}>
        Ask: <em>“What Maya analysis categories do I have access to?”</em> — the assistant should list your enabled
        categories (Shopify, SEO, SEM, …). If nothing happens, see Support below.
      </p>

      {/* What you can do */}
      <SectionHeading>What you can do</SectionHeading>
      <p style={{ fontSize: '0.9rem', color: '#6c757d', margin: '0 0 0.75rem 0' }}>
        Four things to try right away. Each prompt is copy-paste ready.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem' }}>
        {STARTER_PROMPTS.map((p) => <PromptCard key={p.title} item={p} />)}
      </div>

      {/* Category reference */}
      <SectionHeading>Analysis category reference</SectionHeading>
      <p style={{ fontSize: '0.9rem', color: '#6c757d', margin: '0 0 0.75rem 0' }}>
        Each category covers one slice of your marketing stack. Ask any directly, or let the assistant pick the right one
        for a cross-channel question. Access depends on which sources you’ve connected and your subscription — ask
        “What categories do I have?” to see your exact list.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: '560px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', textAlign: 'left' }}>
              <th style={{ padding: '8px 10px', borderBottom: '2px solid #e0e0e0' }}>Category</th>
              <th style={{ padding: '8px 10px', borderBottom: '2px solid #e0e0e0' }}>What it covers</th>
              <th style={{ padding: '8px 10px', borderBottom: '2px solid #e0e0e0' }}>Try asking</th>
            </tr>
          </thead>
          <tbody>
            {CATEGORY_REFERENCE.map((row) => (
              <tr key={row.category}>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #eee', fontWeight: 600, color: '#333', whiteSpace: 'nowrap' }}>{row.category}</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #eee', color: '#555' }}>{row.covers}</td>
                <td style={{ padding: '8px 10px', borderBottom: '1px solid #eee', color: '#37474f', fontStyle: 'italic' }}>{row.ask}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Prompting tips */}
      <SectionHeading>Prompting tips</SectionHeading>
      <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#37474f', fontSize: '0.9rem', lineHeight: 1.8 }}>
        {PROMPTING_TIPS.map((t) => (
          <li key={t.lead}><strong>{t.lead}</strong> {t.body}</li>
        ))}
      </ul>

      {/* Data & privacy */}
      <SectionHeading>Data & privacy</SectionHeading>
      <p style={{ fontSize: '0.9rem', color: '#37474f', lineHeight: 1.6 }}>
        Your data stays in your Maya Insights tenant. The assistant accesses it only through the authenticated MCP
        connection you set up — the connection is per-user and OAuth-gated. Nothing is exfiltrated to model training, and
        you can revoke access at any time from your Maya Insights settings.
      </p>

      {/* Support */}
      <SectionHeading>Support</SectionHeading>
      <p style={{ fontSize: '0.9rem', color: '#37474f', lineHeight: 1.6 }}>
        Docs:{' '}
        <a href={MCP_DOCS_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#739570', textDecoration: 'none' }}>
          docs.mayainsights.com
        </a>
        . For access issues, missing data sources, or anything that doesn’t behave like this guide suggests — reach out to
        your Maya Insights account manager.
      </p>
    </div>
  )
}

export default OnboardingGuide
